import { fieldTypeOf } from '../field-types';
import { countBy, groupBy, upperFirst } from 'lodash';
import { elementName, joinWords, quote } from '../texts/words';
import { patternCompiles } from '../validations/pattern';
import { allElements, defaultOptionIds, findField, optionIdsOf, optionsOf } from './elements';
import type { Element, FieldElement, FormDefinition } from './form-definition';
import {
  comparisonsOf,
  isReference,
  type ComparisonValue,
  type Condition,
  type RangeValidation,
  type ValidationKind,
} from './pieces';
import {
  placesOf,
  referenceTarget,
  sameKindOfValue,
  type Places,
  type ReferenceProblem,
  mayBeBoundOf,
} from './references';
import { hasOptions } from '../field-types/card';
import { DATE_FORMAT, isIsoDate, formatDate } from 'framework/utils/hooks/useDateService';

export interface Reason {
  text: string;
  elementId?: string;
  reference?: { toId: string; problem: ReferenceProblem };
}

type Say = (text: string, elementId?: string, reference?: Reason['reference']) => void;

export const checkFormDefinition = (definition: FormDefinition): Reason[] => {
  const reasons: Reason[] = [];
  const say: Say = (text, elementId, reference) => reasons.push({ text, elementId, reference });
  const places = placesOf(definition);

  if (!definition.title.trim()) say("The form's title is blank.");
  if (Object.keys(definition.customAttributes ?? {}).some(key => !key.trim()))
    say('A custom attribute has a blank key.');
  checkIds(definition, say);
  for (const element of allElements(definition.elements)) {
    if (element.visibleWhen)
      checkCondition(definition, places, element, element.visibleWhen, { kind: 'visibleWhen' }, say);
    if (element.kind === 'field') checkField(definition, places, element, say);
  }
  return reasons;
};

const checkIds = (definition: FormDefinition, say: Say) => {
  for (const [id, elements] of Object.entries(groupBy(allElements(definition.elements), 'id')))
    if (elements.length > 1)
      say(`${upperFirst(joinWords(elements.map(elementName), 'and'))} have the same id, ${quote(id)}.`);
};

const checkOptionIds = (options: { id: string }[], owner: string, say: Say, elementId: string) => {
  for (const [id, count] of Object.entries(countBy(options, 'id')))
    if (count > 1) say(`${upperFirst(owner)} has two options with the id ${quote(id)}.`, elementId);
};

const checkField = (definition: FormDefinition, places: Places, field: FieldElement, say: Say) => {
  const card = fieldTypeOf(field);
  const name = elementName(field);
  const Name = upperFirst(name);

  if (hasOptions(card.valueKind)) {
    checkOptionIds(optionsOf(field), name, say, field.id);
    const ids = optionIdsOf(field);
    for (const id of defaultOptionIds(field))
      if (!ids.has(id)) say(`${Name} has a default value that isn't one of its options (${quote(id)}).`, field.id);
  }

  const kinds = new Set<ValidationKind>();
  for (const validation of field.validations ?? []) {
    const { kind } = validation;
    if (!card.validations.includes(kind)) {
      say(`${Name} has a ${quote(kind)} validation, which its field type doesn't have.`, field.id);
      continue;
    }
    if (kinds.has(kind)) say(`${Name} has two ${quote(kind)} validations.`, field.id);
    kinds.add(kind);
    if (validation.when)
      checkCondition(definition, places, field, validation.when, { kind: 'validationWhen', validation: kind }, say);
    if (kind === 'unique' && !places.get(field.id)?.group)
      say(`${Name} has a ${quote('unique')} validation, but it isn't inside a repeatable group.`, field.id);
    if (kind === 'pattern' && !patternCompiles(validation.regex))
      say(`${Name} has a pattern that isn't a valid regular expression: ${quote(validation.regex)}.`, field.id);
    if (kind === 'range') checkRange(definition, places, field, validation, say);
  }
};

const BOUNDS: Partial<Record<string, string>> = {
  number: "a number or another field's value",
  date: "a date, today or another field's value",
};

const checkRange = (
  definition: FormDefinition,
  places: Places,
  field: FieldElement,
  range: RangeValidation,
  say: Say,
) => {
  const { valueKind } = fieldTypeOf(field);
  const Lead = `The ${quote('range')} validation of ${elementName(field)}`;
  if (range.min === undefined && range.max === undefined)
    say(`${Lead} has neither a ${quote('min')} nor a ${quote('max')}.`, field.id);
  for (const [end, bound] of [
    ['min', range.min],
    ['max', range.max],
  ] as const) {
    if (bound === undefined) continue;
    if (isReference(bound) && bound.kind === 'fieldValue' && (valueKind === 'number' || valueKind === 'date')) {
      const other = referredField(places, field, bound.fieldId, 'bound', say);
      if (other && !mayBeBoundOf(field, other))
        say(`${Lead} takes a bound from ${elementName(other)}, which doesn't hold a ${valueKind}.`, field.id);
      continue;
    }
    const fits =
      valueKind === 'number'
        ? typeof bound === 'number'
        : valueKind === 'date'
          ? (typeof bound === 'string' && isIsoDate(bound)) || (isReference(bound) && bound.kind === 'today')
          : typeof bound === 'number' && Number.isInteger(bound) && bound >= 0;
    const allowed = BOUNDS[valueKind] ?? 'a whole number from 0';
    if (!fits) say(`${Lead} has ${quote(end)} ${shown(definition, bound)}, which isn't ${allowed}.`, field.id);
  }
  const { min, max } = range;
  const bothFixed =
    (typeof min === 'number' && typeof max === 'number') ||
    (typeof min === 'string' && typeof max === 'string' && isIsoDate(min) && isIsoDate(max));
  if (bothFixed && min > max) {
    const [shownMin, shownMax] = [shown(definition, min), shown(definition, max)];
    say(`${Lead} has a ${quote('min')}, ${shownMin}, above its ${quote('max')}, ${shownMax}.`, field.id);
  }
};

type ConditionOf = { kind: 'visibleWhen' } | { kind: 'validationWhen'; validation: ValidationKind };

const checkCondition = (
  definition: FormDefinition,
  places: Places,
  element: Element,
  condition: Condition,
  of: ConditionOf,
  say: Say,
) => {
  const Whose =
    of.kind === 'visibleWhen'
      ? `The Visibility Condition of ${elementName(element)}`
      : `The condition of the ${quote(of.validation)} validation of ${elementName(element)}`;
  if (depth(condition) > 2) say(`${Whose} has groups nested more than one level deep.`, element.id);

  const refer = (toId: string) => referredField(places, element, toId, of, say);
  for (const comparison of comparisonsOf(condition)) {
    const compared = refer(comparison.fieldId);
    if (!compared) continue;
    const card = fieldTypeOf(compared);
    const comparedName = elementName(compared);
    const wrong = (what: string) => say(`${Whose} compares ${comparedName} with ${what}.`, element.id);
    if (!card.operators.includes(comparison.operator)) {
      wrong(`${quote(comparison.operator)}, an operator its field type doesn't have`);
      continue;
    }
    if (!('value' in comparison)) continue;
    const { value } = comparison;
    if (isReference(value)) {
      if (value.kind === 'today') {
        if (card.valueKind !== 'date') wrong('today, but it holds no date');
      } else {
        const other = refer(value.fieldId);
        if (other && !sameKindOfValue(compared, other))
          wrong(`${elementName(other)}, which holds another kind of value`);
      }
      continue;
    }
    const problem = valueProblem(definition, compared, value);
    if (problem !== undefined) wrong(problem);
  }
};

const valueProblem = (
  definition: FormDefinition,
  compared: FieldElement,
  value: ComparisonValue,
): string | undefined => {
  switch (fieldTypeOf(compared).valueKind) {
    case 'option':
    case 'options': {
      if (!Array.isArray(value)) return `${shown(definition, value)}, which isn't a list of its options`;
      if (value.length === 0) return 'no options';
      const ids = optionIdsOf(compared);
      const missing = value.filter(id => !ids.has(id));
      return missing.length ? `an option it doesn't have (${missing.map(quote).join(', ')})` : undefined;
    }
    case 'number':
      return typeof value === 'number' ? undefined : `${shown(definition, value)}, which isn't a number`;
    case 'date':
      return typeof value === 'string' && isIsoDate(value)
        ? undefined
        : `${shown(definition, value)}, which isn't a date`;
    case 'text':
      if (typeof value !== 'string') return `${shown(definition, value)}, which isn't text`;
      return value.trim() ? undefined : 'blank text';
    default:
      return shown(definition, value);
  }
};

const depth = (condition: Condition): number => {
  return condition.kind === 'comparison' ? 0 : 1 + Math.max(0, ...condition.conditions.map(depth));
};

const referredField = (
  places: Places,
  from: Element,
  toId: string,
  of: ConditionOf | 'bound',
  say: Say,
): FieldElement | undefined => {
  const { field, problem } = referenceTarget(places, from.id, toId, of === 'bound' ? of : of.kind);
  if (problem) say(referenceWords(places, from, toId, problem, of), from.id, { toId, problem });
  return field;
};

const referenceWords = (
  places: Places,
  from: Element,
  toId: string,
  problem: ReferenceProblem,
  of: ConditionOf | 'bound',
): string => {
  const name = elementName(from);
  const lead =
    of === 'bound'
      ? `the ${quote('range')} validation of ${name} takes a bound from`
      : of.kind === 'visibleWhen'
        ? `${name} depends on`
        : `the ${quote(of.validation)} validation of ${name} depends on`;
  const to = places.get(toId)?.element;
  const toName = to ? elementName(to) : quote(toId);
  const group = places.get(toId)?.group;
  const words: Record<ReferenceProblem, string> = {
    missing: `a field that isn't in the form (${quote(toId)})`,
    notAField: `${toName}, which isn't a field`,
    self: `${toName} itself`,
    inside: `${toName}, which is inside it`,
    container: `${toName}, which ${name} stands in`,
    intoGroup: `${toName}, which is inside the repeatable group ${group ? elementName(group) : ''}`.trimEnd(),
    below: `${toName}, which comes after it in the form`,
  };
  return `${upperFirst(lead)} ${words[problem]}.`;
};

const shown = (definition: FormDefinition, value: ComparisonValue): string => {
  if (Array.isArray(value)) return value.length ? value.map(item => quote(String(item))).join(', ') : 'an empty list';
  if (isReference(value)) {
    if (value.kind === 'today') return 'today';
    const field = findField(definition, value.fieldId);
    return field ? elementName(field) : quote(value.fieldId);
  }
  if (typeof value === 'string')
    return isIsoDate(value) ? (formatDate(value, DATE_FORMAT.INPUT_VALUE)?.trim() ?? value) : quote(value);
  return String(value);
};
